import React, { useEffect, useMemo } from 'react';
import { useOutsideClick } from '~/hooks/useOutsideClick';
import useWindowDimensions from '~/hooks/useWindowDimensions';
import SVGIcon from '../ui/Icons/SVGIcon';
import { Button } from './buttons/Button';

type Position = 'left' | 'top-left' | 'top' | 'top-right' | 'right' | 'bottom-right' | 'bottom' | 'bottom-left';

interface PopoverProps {
  isOpen: boolean;
  onClose: () => void;
  arrowPosition: Position;
  popoverPosition: Position;
  triggerRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  width?: number;
  title?: string;
}

const SPACER = 8;

const Popover = ({
  isOpen,
  onClose,
  arrowPosition,
  popoverPosition,
  triggerRef,
  children,
  width = 416,
  title,
}: PopoverProps) => {
  const popoverRef = useOutsideClick(onClose, null, triggerRef);
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth && windowWidth < 768;

  const getPositionStyles = useMemo(() => {
    return (triggerRect: DOMRect, popoverRect: DOMRect) => {
      let bottom = null;
      let top = null;
      let left = null;
      let right = null;

      if (!isMobile) {
        switch (popoverPosition) {
          case 'left':
            top = (triggerRect.height - popoverRect.height) / 2;
            left = -SPACER - popoverRect.width;
            break;
          case 'top-left':
            bottom = triggerRect.height + SPACER;
            left = 0;
            break;
          case 'top':
            bottom = triggerRect.height + SPACER;
            left = -((triggerRect.width - popoverRect.width) / 2);
            break;
          case 'top-right':
            bottom = triggerRect.height + SPACER;
            right = 0;
            break;
          case 'right':
            top = (triggerRect.height - popoverRect.height) / 2;
            right = -SPACER - popoverRect.width;
            break;
          case 'bottom-right':
            top = triggerRect.height + SPACER;
            right = 0;
            break;
          case 'bottom':
            top = triggerRect.height + SPACER;
            left = (triggerRect.width - popoverRect.width) / 2;
            break;
          case 'bottom-left':
            top = triggerRect.height + SPACER;
            left = 0;
            break;
        }
      }

      return { bottom, top, left, right };
    };
  }, [popoverPosition, isMobile]);

  useEffect(() => {
    if (isOpen && popoverRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const { bottom, top, left, right } = getPositionStyles(triggerRect, popoverRect);

      popoverRef.current.style.bottom = null;
      popoverRef.current.style.top = null;
      popoverRef.current.style.left = null;
      popoverRef.current.style.right = null;

      if (bottom !== null) popoverRef.current.style.bottom = `${bottom}px`;
      if (top !== null) popoverRef.current.style.top = `${top}px`;
      if (left !== null) popoverRef.current.style.left = `${left}px`;
      if (right !== null) popoverRef.current.style.right = `${right}px`;
    }
  }, [isOpen, popoverPosition, triggerRef, popoverRef, getPositionStyles]);

  const popoverClassName = `fo-popover ${isOpen ? 'show' : ''} arrow-${arrowPosition} ${isMobile ? 'mobile' : ''}`;

  return (
    <>
      <div
        className={`fo-popover-backdrop ${isMobile && isOpen ? 'show' : ''}`}
        // necessary to prevent element behind backdrop from being triggered
        // we use onTouchEnd because touchStart event is being overwritten by useOutsideClick
        onTouchEnd={(e) => {
          e.preventDefault();
        }}
      />
      <div
        ref={popoverRef}
        className={popoverClassName}
        style={{ width: isMobile ? 'initial' : width }}
      >
        <div className="arrow" />
        {title && (
          <div className="fo-popover-header">
            <div className="do-display-5 -medium">{title}</div>
            <Button
              type="button"
              isIconButton={true}
              size="sm"
              variant="primaryGhost"
            >
              <SVGIcon
                iconType="cross"
                onClick={onClose}
              />
            </Button>
          </div>
        )}

        <div className="fo-popover-content">{children}</div>
      </div>
    </>
  );
};

export default Popover;
